import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Eraser, Check } from "lucide-react";
import { toast } from "sonner";

export default function SignaturePad({
  onCancel,
  onSave,
  saving,
  courseName,
  studentName,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const [hasSignature, setHasSignature] = useState(false);

  // Configure HiDPI canvas.
  // Refs are intentionally not in the dependency array — they are stable
  // and only read inside the effect. This effect should run exactly once
  // on mount (and re-attach the resize listener for the component's life).
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      // Preserve drawing on resize: snapshot
      const tmp = document.createElement("canvas");
      tmp.width = canvas.width;
      tmp.height = canvas.height;
      const tctx = tmp.getContext("2d");
      tctx.drawImage(canvas, 0, 0);

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = "#0A0A0A";
      // Restore previous drawing (scaled)
      if (tmp.width && tmp.height) {
        ctx.drawImage(tmp, 0, 0, rect.width, rect.height);
      }
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // Refs (canvasRef/containerRef) son estables. Effect debe ejecutarse
    // SOLO al montar para configurar el canvas HiDPI.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX =
      e.touches?.[0]?.clientX ?? e.clientX ?? 0;
    const clientY =
      e.touches?.[0]?.clientY ?? e.clientY ?? 0;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (!hasSignature) setHasSignature(true);
  };

  const end = (e) => {
    e?.preventDefault?.();
    drawingRef.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const save = () => {
    if (!hasSignature) {
      toast.error("Firma vacía. Dibuja tu firma antes de guardar.");
      return;
    }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-white flex flex-col"
      data-testid="signature-modal"
    >
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white shrink-0">
        <div className="px-4 md:px-8 h-16 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              Firma de Asistencia
            </div>
            <div className="text-sm font-semibold text-zinc-950 truncate">
              {studentName || "Alumno"}
              {courseName ? (
                <span className="text-zinc-500 font-normal"> · {courseName}</span>
              ) : null}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
            data-testid="signature-cancel-button"
          >
            <X className="h-4 w-4" />
            Cancelar
          </button>
        </div>
      </header>

      {/* Canvas area */}
      <div
        ref={containerRef}
        className="flex-1 relative bg-white overflow-hidden"
      >
        <canvas
          ref={canvasRef}
          className="signature-canvas"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
        {!hasSignature && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="text-center select-none">
              <div className="heading-font text-zinc-200 text-5xl md:text-7xl font-extrabold tracking-tight">
                Firma aquí
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-400">
                Usa el dedo o el ratón
              </div>
            </div>
          </div>
        )}

        {/* Baseline guide */}
        <div className="pointer-events-none absolute left-8 right-8 bottom-16 border-b border-dashed border-zinc-200" />
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white shrink-0">
        <div className="px-4 md:px-8 py-4 grid grid-cols-2 gap-3">
          <Button
            onClick={clear}
            variant="outline"
            className="h-14 text-base font-semibold border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-100 rounded-md"
            data-testid="signature-clear-button"
          >
            <Eraser className="mr-2 h-5 w-5" />
            Limpiar
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="h-14 text-base font-semibold bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
            data-testid="signature-save-button"
          >
            <Check className="mr-2 h-5 w-5" />
            {saving ? "Guardando…" : "Guardar firma"}
          </Button>
        </div>
      </footer>
    </div>
  );
}
